import React from 'react';

export interface BJJBeltProps {
  /** Belt color name */
  color: 'white' | 'blue' | 'purple' | 'brown' | 'black' | string;
  /** Number of stripes (0-10) */
  stripes?: number;
  /** Class for additional styling */
  className?: string;
  /** Optional width override */
  width?: string;
  /** Optional height override */
  height?: string;
}

/**
 * A component that renders a Brazilian Jiu-Jitsu belt with proper styling and stripe display
 */
export const BJJBelt: React.FC<BJJBeltProps> = ({
  color = 'white',
  stripes = 0,
  className = '',
  width = '100%',
  height = '30px',
}) => {
  // Ensure stripes is a valid number - parse from any type
  let stripeCount: number = 0;
  
  // Handle number or string inputs for stripes
  if (typeof stripes === 'number') {
    stripeCount = stripes;
  } else if (typeof stripes === 'string') {
    // Try to parse it as a number
    const parsed = parseInt(stripes, 10);
    if (!isNaN(parsed)) {
      stripeCount = parsed;
    }
  }
  
  // Normalize color name to lowercase and trim
  const normalizedColor = (color || 'white').toLowerCase().trim();
  
  // Define exact colors to match original Marune app
  const getBeltColorCode = (colorName: string): string => {
    if (colorName.includes('white')) return '#FFFFFF';
    if (colorName.includes('blue')) return '#0000AA';
    if (colorName.includes('purple')) return '#9900CC';
    if (colorName.includes('brown')) return '#663300';
    if (colorName.includes('black')) return '#000000';
    if (colorName.includes('green')) return '#006600';
    if (colorName.includes('yellow')) return '#FFCC00';
    if (colorName.includes('orange')) return '#FF6600';
    if (colorName.includes('red')) return '#CC0000';
    
    // Default fallback to white if nothing matches
    return '#FFFFFF';
  };
  
  // Get the actual belt color
  const beltColor = getBeltColorCode(normalizedColor);
  
  // Validate stripe count to be between 0-10
  const validStripes = Math.max(0, Math.min(10, stripeCount));
  
  // Check if this is a black belt with stripes (needs red bar)

  
  
  return (
    <div 
      className={`relative rounded-sm overflow-hidden ${className}`}
      style={{ 
        width, 
        height 
      }}
      data-belt-type="bjj"
      data-belt-color={normalizedColor}
      data-stripe-count={validStripes}
    >
      {/* Main belt body */}
      <div 
        className="absolute inset-0" 
        style={{ 
          backgroundColor: beltColor,
          // Add border for white belt only
          border: normalizedColor.includes('white') ? '1px solid #CCCCCC' : 'none'
        }}
      />
      
      {/* Black/Red bar for stripe area - ALL belts get this */}
      <div 
        className="absolute right-0 top-0 bottom-0"
        style={{ 
          width: '30%',
          minWidth: '35px',
          backgroundColor: normalizedColor.includes('black') && validStripes > 0 ? '#FF0000' : '#000000',
          zIndex: 1
        }}
      />
      
      {/* Stripes container - must be on top */}
      {validStripes > 0 && (
        <div 
          className="absolute right-0 top-0 bottom-0 flex items-center justify-center"
          style={{ 
            width: '30%',
            minWidth: '35px',
            paddingRight: '8px',
            paddingLeft: '8px',
            zIndex: 2
          }}
        >
          {/* Render actual stripes */}
          <div className="flex items-center justify-center" style={{ gap: '2px' }}>
            {Array.from({ length: validStripes }).map((_, index) => (
              <div 
                key={index} 
                className="bg-white" 
                style={{ 
                  width: '4px', 
                  minWidth: '2px',
                  height: '70%',
                  minHeight: '10px',
                  borderRadius: '0.5px',
                  boxShadow: '0 0.5px 1px rgba(0,0,0,0.5)'
                }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BJJBelt;